import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent as RKeyboardEvent, PointerEvent as RPointerEvent } from 'react';

/**
 * EL RECORTADOR de la app (0.53.0): una foto completa con un MARCO de la proporción que se pida encima, que se arrastra
 * (mouse, dedo o flechas; Shift = paso grande) y se acerca (la rueda, pellizcar con dos dedos, o el control "Alejar /
 * Acercar", hasta 4×). Lo usan la imagen de Mensajes y avisos (2:1, `ImagenAviso.tsx`) y las de Promociones (16:9 la
 * tarjeta, 4:5 al abrirla, 2:1 la notificación). Puede marcar la ZONA DE TEXTO (lo que en la app queda debajo del título
 * o tapado por el contenido). Sin librerías: el canvas del navegador recorta y baja la calidad hasta que quepa.
 *
 * Todo se mide en pixeles de la foto original: `Rec = { cx, cy, z }` (centro del marco y cuánto se acercó) y `Caja` la parte
 * que queda. Para guardar se normaliza a 0..1 (`normalDe`) y al volver a abrir se regresa (`recDeNormal`): el marco queda
 * donde se dejó.
 */
export type Fuente = { src: string; w: number; h: number; nombre: string };
export type Caja = { x: number; y: number; w: number; h: number };
export type Rec = { cx: number; cy: number; z: number };
export type Normal = { x: number; y: number; w: number; h: number };
export const ZOOM_MAX = 4;

const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));
/** El marco más grande de esa proporción que cabe en la foto (acercar = 1). */
export function cajaBase(f: Fuente, aspecto: number) { return f.w / f.h >= aspecto ? { w: f.h * aspecto, h: f.h } : { w: f.w, h: f.w / aspecto }; }
/** La parte que queda, en pixeles de la foto (nunca se sale de la foto). */
export function cajaDe(f: Fuente, r: Rec, aspecto: number): Caja {
  const b = cajaBase(f, aspecto); const w = b.w / r.z, h = b.h / r.z;
  return { x: clamp(r.cx - w / 2, 0, f.w - w), y: clamp(r.cy - h / 2, 0, f.h - h), w, h };
}
export const acotar = (f: Fuente, r: Rec, aspecto: number): Rec => {
  const z = clamp(r.z, 1, ZOOM_MAX); const c = cajaDe(f, { ...r, z }, aspecto); return { z, cx: c.x + c.w / 2, cy: c.y + c.h / 2 };
};
export const recInicial = (f: Fuente): Rec => ({ cx: f.w / 2, cy: f.h / 2, z: 1 });
export const normalDe = (f: Fuente, c: Caja): Normal => {
  const n = (v: number) => Math.round(clamp(v, 0, 1) * 1e6) / 1e6;
  return { x: n(c.x / f.w), y: n(c.y / f.h), w: n(c.w / f.w), h: n(c.h / f.h) };
};
/** De un recorte guardado (0..1) al marco: el centro y cuánto se acercó (si la proporción no cuadra, gana el ancho). */
export function recDeNormal(f: Fuente, n: Normal | null | undefined, aspecto: number): Rec {
  if (!n || !(n.w > 0)) return recInicial(f);
  const b = cajaBase(f, aspecto);
  return acotar(f, { cx: (n.x + n.w / 2) * f.w, cy: (n.y + n.h / 2) * f.h, z: b.w / (n.w * f.w) }, aspecto);
}

export function cargarImagen(src: string): Promise<HTMLImageElement> {
  return new Promise((ok, mal) => { const img = new Image(); img.onload = () => ok(img); img.onerror = () => mal(new Error('no se pudo abrir')); img.src = src; });
}
const esHeic = (f: File) => /hei[cf]/i.test(f.type) || /\.hei[cf]$/i.test(f.name);
/** Abre el archivo como imagen. Un HEIC que el navegador no abre (casi todos menos Safari) se intenta convertir aquí. */
export async function abrirArchivo(file: File, maxMB = 40): Promise<Fuente> {
  if (!/^image\//.test(file.type) && !/\.(jpe?g|png|webp|hei[cf])$/i.test(file.name)) throw new Error('Ese archivo no es una imagen: elige un JPG, PNG o WebP.');
  if (file.size > maxMB * 1024 * 1024) throw new Error(`Esa imagen pesa más de ${maxMB} MB: elige una más ligera.`);
  const url = URL.createObjectURL(file);
  try { const img = await cargarImagen(url); return { src: url, w: img.naturalWidth, h: img.naturalHeight, nombre: file.name }; }
  catch { URL.revokeObjectURL(url); }
  if (!esHeic(file)) throw new Error('Este navegador no pudo abrir esa imagen. Elige un JPG, PNG o WebP.');
  try {
    const mod = await import('heic2any');
    const heic2any = (mod as unknown as { default: (o: { blob: Blob; toType: string; quality?: number }) => Promise<Blob | Blob[]> }).default;
    const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
    const jpg = Array.isArray(out) ? out[0] : out;
    const u2 = URL.createObjectURL(jpg); const img = await cargarImagen(u2);
    return { src: u2, w: img.naturalWidth, h: img.naturalHeight, nombre: file.name };
  } catch {
    throw new Error('Este navegador no puede abrir fotos HEIC (las del iPhone). Desde el iPhone, compártela como JPG (o ábrela en Safari) y vuelve a elegirla.');
  }
}
/** Abre una imagen ya guardada (por la API del mismo sitio: un blob local no "ensucia" el canvas). */
export async function abrirBlob(b: Blob, nombre: string): Promise<Fuente> {
  const url = URL.createObjectURL(b); const img = await cargarImagen(url);
  return { src: url, w: img.naturalWidth, h: img.naturalHeight, nombre };
}
const pesa = (d: string) => Math.floor((d.length - d.indexOf(',') - 1) * 0.75);
/** Un canvas a JPEG con la calidad que haga falta para no pasar de `maxKB`. */
function aJpeg(cv: HTMLCanvasElement, maxKB: number) {
  let data = ''; let bytes = 0;
  for (const q of [0.88, 0.84, 0.8, 0.74, 0.68, 0.62, 0.56, 0.5, 0.44, 0.38, 0.32]) {
    data = cv.toDataURL('image/jpeg', q); bytes = pesa(data);
    if (bytes <= maxKB * 1024) break;
  }
  return { data, bytes };
}
/** El recorte en un JPEG de `ancho × alto`, con la calidad que haga falta para no pasar de `maxKB`. */
export async function generarJpeg(f: Fuente, c: Caja, salida: { ancho: number; alto: number; maxKB: number }): Promise<{ data: string; bytes: number }> {
  const img = await cargarImagen(f.src);
  const cv = document.createElement('canvas'); cv.width = salida.ancho; cv.height = salida.alto;
  const g = cv.getContext('2d')!;
  g.fillStyle = '#ffffff'; g.fillRect(0, 0, salida.ancho, salida.alto); // un PNG transparente no sale negro
  g.imageSmoothingEnabled = true; g.imageSmoothingQuality = 'high';
  g.drawImage(img, c.x, c.y, c.w, c.h, 0, 0, salida.ancho, salida.alto);
  return aJpeg(cv, salida.maxKB);
}
/** La foto completa reducida (lado ≤ `lado`, ≤ `maxKB`): la que se guarda para volver a recortar después. */
export async function reducirOriginal(f: Fuente, lado = 3000, maxKB = 4096): Promise<{ data: string; bytes: number; ancho: number; alto: number }> {
  const img = await cargarImagen(f.src);
  const k = Math.min(1, lado / Math.max(f.w, f.h));
  const w = Math.max(1, Math.round(f.w * k)), h = Math.max(1, Math.round(f.h * k));
  const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
  const g = cv.getContext('2d')!; g.fillStyle = '#ffffff'; g.fillRect(0, 0, w, h);
  g.imageSmoothingEnabled = true; g.imageSmoothingQuality = 'high'; g.drawImage(img, 0, 0, w, h);
  return { ...aJpeg(cv, maxKB), ancho: w, alto: h };
}

/**
 * La foto completa con el marco encima. `zona` = desde dónde (0..1 del alto del marco) queda el texto o lo tapa el
 * contenido: se marca con una franja y su leyenda. Conserva los `data-av-*` de siempre (las pruebas de Mensajes y avisos);
 * `espacio` va como `data-espacio` para distinguir los marcos de Promociones.
 */
export function Recortador({ fuente, rec, onRec, aspecto, zona, espacio, maxAlto }: {
  fuente: Fuente; rec: Rec; onRec: (r: Rec) => void; aspecto: number; zona?: { desde: number; texto: string } | null; espacio?: string; maxAlto?: number;
}) {
  const caja = useRef<HTMLDivElement>(null);
  const lienzo = useRef<HTMLDivElement>(null);
  const [ancho, setAncho] = useState(0);
  const recRef = useRef(rec); recRef.current = rec;
  const dedos = useRef(new Map<number, { x: number; y: number }>());
  const arrastre = useRef<{ x: number; y: number; cx: number; cy: number } | null>(null);
  const pellizco = useRef<{ d: number; z: number } | null>(null);
  useEffect(() => {
    const el = caja.current; if (!el) return;
    const ro = new ResizeObserver(() => setAncho(el.clientWidth)); ro.observe(el); setAncho(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const alto = maxAlto || (typeof window !== 'undefined' && window.innerWidth < 600 ? 260 : 340);
  const dispW = Math.max(1, Math.min(ancho || 1, alto * fuente.w / fuente.h));
  const k = dispW / fuente.w; const dispH = fuente.h * k;
  const c = cajaDe(fuente, rec, aspecto);
  const base = cajaBase(fuente, aspecto);
  const mover = (r: Rec) => onRec(acotar(fuente, r, aspecto));
  // La rueda acerca y aleja sin mover la página (un listener no pasivo: el de React no puede cancelar el scroll).
  useEffect(() => {
    const el = lienzo.current; if (!el) return;
    const rueda = (e: WheelEvent) => {
      e.preventDefault();
      const r = recRef.current; mover({ ...r, z: r.z * Math.exp(-e.deltaY * 0.0015) });
    };
    el.addEventListener('wheel', rueda, { passive: false });
    return () => el.removeEventListener('wheel', rueda);
  }, [fuente, aspecto]);
  function abajo(e: RPointerEvent<HTMLDivElement>) {
    dedos.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture(e.pointerId); e.preventDefault();
    if (dedos.current.size === 2) {
      const [a, b] = [...dedos.current.values()];
      pellizco.current = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, z: rec.z }; arrastre.current = null; return;
    }
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / k, py = (e.clientY - r.top) / k;
    let start = rec;
    // Tocar fuera del marco lo lleva ahí; dentro, se arrastra desde donde está.
    if (px < c.x || px > c.x + c.w || py < c.y || py > c.y + c.h) { start = { ...rec, cx: px, cy: py }; mover(start); }
    arrastre.current = { x: e.clientX, y: e.clientY, cx: start.cx, cy: start.cy };
  }
  function moverDedo(e: RPointerEvent<HTMLDivElement>) {
    if (!dedos.current.has(e.pointerId)) return;
    dedos.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pellizco.current && dedos.current.size >= 2) {
      const [a, b] = [...dedos.current.values()];
      mover({ ...rec, z: pellizco.current.z * (Math.hypot(a.x - b.x, a.y - b.y) / pellizco.current.d) }); return;
    }
    const a = arrastre.current; if (!a) return;
    mover({ ...rec, cx: a.cx + (e.clientX - a.x) / k, cy: a.cy + (e.clientY - a.y) / k });
  }
  function soltar(e: RPointerEvent<HTMLDivElement>) {
    dedos.current.delete(e.pointerId);
    if (dedos.current.size < 2) pellizco.current = null;
    if (!dedos.current.size) arrastre.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ya se soltó */ }
  }
  function tecla(e: RKeyboardEvent) {
    const paso = (e.shiftKey ? 0.1 : 0.02) * base.w;
    const m: Record<string, () => Rec> = {
      ArrowLeft: () => ({ ...rec, cx: c.x + c.w / 2 - paso }), ArrowRight: () => ({ ...rec, cx: c.x + c.w / 2 + paso }),
      ArrowUp: () => ({ ...rec, cy: c.y + c.h / 2 - paso }), ArrowDown: () => ({ ...rec, cy: c.y + c.h / 2 + paso }),
      '+': () => ({ ...rec, z: rec.z + 0.1 }), '=': () => ({ ...rec, z: rec.z + 0.1 }), '-': () => ({ ...rec, z: rec.z - 0.1 }),
    };
    if (m[e.key]) { e.preventDefault(); mover(m[e.key]()); }
  }
  const btnZ: CSSProperties = { padding: '3px 10px', fontSize: 12.5, minWidth: 0 };
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div ref={caja} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <div ref={lienzo} data-av-recorte data-espacio={espacio || ''} onPointerDown={abajo} onPointerMove={moverDedo} onPointerUp={soltar} onPointerCancel={soltar}
          style={{ position: 'relative', width: dispW, height: dispH, overflow: 'hidden', borderRadius: 8, touchAction: 'none', userSelect: 'none', cursor: 'grab', background: '#222' }}>
          <img src={fuente.src} alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block', pointerEvents: 'none' }} />
          <div tabIndex={0} role="group" onKeyDown={tecla} data-av-caja data-x={Math.round(c.x)} data-y={Math.round(c.y)} data-w={Math.round(c.w)} data-h={Math.round(c.h)}
            aria-label="La parte que queda: arrástrala o muévela con las flechas (Shift para pasos grandes); + y − para acercar o alejar"
            style={{ position: 'absolute', left: c.x * k, top: c.y * k, width: c.w * k, height: c.h * k, boxShadow: '0 0 0 9999px rgba(30, 24, 30, 0.55)', outline: '2px solid #fff', outlineOffset: -1, cursor: 'move',
              backgroundImage: 'linear-gradient(rgba(255,255,255,.35) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.35) 1px, transparent 1px)', backgroundSize: '33.333% 33.333%', backgroundPosition: '-1px -1px' }}>
            {zona && <div data-zona-texto style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${(1 - zona.desde) * 100}%`, borderTop: '1.5px dashed rgba(255,255,255,.9)',
              background: 'linear-gradient(to top, rgba(20,14,8,.55), rgba(20,14,8,.15))', display: 'flex', alignItems: 'flex-end', padding: '4px 6px', pointerEvents: 'none' }}>
              <span style={{ color: '#fff', fontSize: 11, fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,.6)', lineHeight: 1.2 }}>{zona.texto}</span>
            </div>}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <button type="button" className="btn-ghost" style={btnZ} onClick={() => mover({ ...rec, z: rec.z - 0.25 })} disabled={rec.z <= 1.001} data-zoom-menos aria-label="Alejar">Alejar</button>
        <input type="range" min={1} max={ZOOM_MAX} step={0.05} value={rec.z} onChange={(e) => mover({ ...rec, z: Number(e.target.value) })} data-av-zoom aria-label="Acercar o alejar"
          style={{ flex: 1, padding: 0, border: 0, background: 'transparent', boxShadow: 'none', accentColor: 'var(--gold-dark)', minWidth: 0 }} />
        <button type="button" className="btn-ghost" style={btnZ} onClick={() => mover({ ...rec, z: rec.z + 0.25 })} disabled={rec.z >= ZOOM_MAX - 0.001} data-zoom-mas aria-label="Acercar">Acercar</button>
      </div>
      <span className="muted" style={{ fontSize: 12 }}>Arrastra el marco para elegir qué parte queda (con dos dedos, o la rueda del mouse, se acerca). Lo de afuera se corta.</span>
    </div>
  );
}

/** Lo que enseña una vista previa: la imagen ya guardada (url), o el recorte mientras se elige (en vivo). */
export type VistaImagen = { tipo: 'url'; url: string; respaldo?: string | null } | { tipo: 'recorte'; fuente: Fuente; caja: Caja } | null;
/** Una imagen de una vista previa: la guardada (url) o el recorte en vivo (un fondo posicionado: no se genera nada). */
export function ImgRecortada({ vista, aspecto = 2, style, datos, ancho, alto }: { vista: VistaImagen; aspecto?: number; style?: CSSProperties; datos?: string; ancho?: number; alto?: number }) {
  const [fallo, setFallo] = useState(false);
  useEffect(() => setFallo(false), [vista && vista.tipo === 'url' ? vista.url : null]);
  if (!vista) return null;
  const s: CSSProperties = { width: '100%', aspectRatio: String(aspecto), display: 'block', background: '#ece6da', ...style };
  if (vista.tipo === 'url') {
    const src = fallo && vista.respaldo ? vista.respaldo : vista.url;
    return <img src={src} alt="" width={ancho} height={alto} style={{ ...s, height: style?.height ?? 'auto', objectFit: 'cover' }} data-av-vista-img={datos || ''} onError={() => setFallo(true)} />;
  }
  const { fuente: f, caja: c } = vista;
  const pos = (x: number, total: number, w: number) => (total - w > 0.5 ? (x / (total - w)) * 100 : 0);
  return <div data-av-vista-img={datos || ''} style={{ ...s, backgroundImage: `url("${f.src}")`, backgroundRepeat: 'no-repeat', backgroundSize: `${(f.w / c.w) * 100}% auto`, backgroundPosition: `${pos(c.x, f.w, c.w)}% ${pos(c.y, f.h, c.h)}%` }} />;
}
